import "@/App.css";
import { Suspense, lazy, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth, probablyAuthed } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { SavedProvider } from "@/context/SavedContext";
import { Toaster } from "@/components/ui/sonner";
import BrandedLoader from "@/components/BrandedLoader";
import InstallPrompt from "@/components/InstallPrompt";
import ScrollToTop from "@/components/ScrollToTop";
import LogoutOverlay from "@/components/LogoutOverlay";
import ErrorBoundary from "@/components/ErrorBoundary";
import { isStandalone } from "@/lib/pwa";
import DisaWidget from "@/components/disa/DisaWidget";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";

// Heavy authenticated pages — code-split for faster initial load.
const ChildWizard = lazy(() => import("@/pages/ChildWizard"));
const CompleteProfile = lazy(() => import("@/pages/CompleteProfile"));
const ManageChild = lazy(() => import("@/pages/ManageChild"));
const ChildProfileSummary = lazy(() => import("@/pages/ChildProfileSummary"));
const EditProfile = lazy(() => import("@/pages/EditProfile"));
const DomainDetail = lazy(() => import("@/pages/DomainDetail"));
const Assessment = lazy(() => import("@/pages/Assessment"));
const Report = lazy(() => import("@/pages/Report"));
const DeepDive = lazy(() => import("@/pages/DeepDive"));
const Activities = lazy(() => import("@/pages/Activities"));
const Home = lazy(() => import("@/pages/Home"));
const More = lazy(() => import("@/pages/More"));
const ActivityDetail = lazy(() => import("@/pages/ActivityDetail"));
const Library = lazy(() => import("@/pages/Library"));
const Progress = lazy(() => import("@/pages/Progress"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const Courses = lazy(() => import("@/pages/Courses"));
const CoursePreview = lazy(() => import("@/pages/CoursePreview"));
const CourseLearn = lazy(() => import("@/pages/CourseLearn"));
const Settings = lazy(() => import("@/pages/Settings"));
const Subscription = lazy(() => import("@/pages/Subscription"));
const DownloadCenter = lazy(() => import("@/pages/DownloadCenter"));
const MonthlyReport = lazy(() => import("@/pages/MonthlyReport"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const Terms = lazy(() => import("@/pages/Terms"));
const About = lazy(() => import("@/pages/About"));
const FAQs = lazy(() => import("@/pages/FAQs"));
const HelpCenter = lazy(() => import("@/pages/HelpCenter"));
const Resources = lazy(() => import("@/pages/Resources"));
const ExploreActivities = lazy(() => import("@/pages/ExploreActivities"));
const Books = lazy(() => import("@/pages/Books"));
const BookReview = lazy(() => import("@/pages/BookReview"));
const FreeScreening = lazy(() => import("@/pages/free-screening/FreeScreening"));
const MilestonesPage = lazy(() => import("@/pages/milestones-page/MilestonesPage"));
const ChildAgeCalculator = lazy(() => import("@/pages/cac-page/ChildAgeCalculator"));
const VideoAssessment = lazy(() => import("@/pages/video-assessment"));
const Videos = lazy(() => import("@/pages/Videos"));
const SuccessStories = lazy(() => import("@/pages/SuccessStories"));
const Admin = lazy(() => import("@/pages/Admin"));
const Disa = lazy(() => import("@/pages/Disa"));
const NotFound = lazy(() => import("@/pages/NotFound"));
import AppShell from "@/components/AppShell";
import PublicShell from "@/components/PublicShell";

const PageFallback = () => <BrandedLoader visible title="Loading…" subtitle="Just a moment" />;

function PrivateRoute({ children, admin = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <BrandedLoader visible title="Loading…" subtitle="Securing your session" />;
  if (!user) {
    const state = { from: location };
    // Pass a specific message when a guest tries to access the full DISA chat page
    if (location.pathname === "/disa") {
      state.disaMessage = "Please log in to access the full DISA therapist experience.";
    }
    return <Navigate to="/login" state={state} replace />;
  }
  if (admin && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function LandingRoute() {
  // Logged-in users skip the marketing page and go straight to their dashboard
  // (admins to /admin, parents to /activities). When launched as an installed
  // PWA, anonymous users go straight to /login instead of the landing page.
  //
  // While auth is bootstrapping we have two cases:
  //   - The synchronous session hint (`probablyAuthed()`) says "the last
  //     known user was signed in" → render BrandedLoader so the marketing
  //     page never flashes before the redirect to /activities or /admin.
  //   - No hint at all → it's almost certainly an anonymous visitor, so
  //     render Landing immediately to keep the marketing-first-paint snappy.
  const { user, loading } = useAuth();
  if (loading) {
    if (probablyAuthed()) return <BrandedLoader visible title="Welcome back" subtitle="Loading your dashboard…" />;
    return <Landing />;
  }
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/activities"} replace />;
  if (isStandalone()) return <Navigate to="/login" replace />;
  return <Landing />;
}

/**
 * Pages that should live INSIDE the dashboard shell for authenticated users
 * but stay accessible to anonymous visitors via the public marketing shell.
 * Today this is just `/courses` — logged-in parents asked to be able to
 * browse and resume courses without bouncing out of the dashboard nav.
 */
function CoursesShellGate() {
  const { user, loading } = useAuth();
  if (loading) return <BrandedLoader visible title="Loading…" subtitle="Just a moment" />;
  return user ? <AppShell /> : <PublicShell />;
}

/**
 * GlobalWidgets — renders the DISA floating chat widget for ALL users
 * (logged-in and guest). Hidden on auth pages, /disa full-page, and /admin.
 */
function GlobalWidgets() {
  const { pathname } = useLocation();
  const HIDDEN = ["/login", "/signup", "/reset-password", "/verify-email", "/disa"];
  if (HIDDEN.includes(pathname) || pathname.startsWith("/admin")) return null;
  return <DisaWidget />;
}

/**
 * Root-level logout overlay — lives OUTSIDE PrivateRoute/AppShell so it persists
 * through the AppShell unmount that happens when setUser(null) is called on logout.
 * AppShell dispatches "kpp:logout:start" instead of rendering its own overlay.
 */
function RootLogoutOverlay() {
  const [show, setShow] = useState(false);
  const [name, setName] = useState("");

  useEffect(() => {
    const onStart = (e) => { setShow(true); setName(e.detail?.name ?? ""); };
    window.addEventListener("kpp:logout:start", onStart);
    return () => window.removeEventListener("kpp:logout:start", onStart);
  }, []);

  // Auto-clear after logout + navigation completes. Dispatches `kpp:login:reveal`
  // first so Login.jsx can start its fade-in in sync with the overlay unmounting.
  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => {
      window.dispatchEvent(new CustomEvent("kpp:login:reveal"));
      setShow(false);
    }, 2500);
    return () => clearTimeout(t);
  }, [show]);

  return show ? <LogoutOverlay name={name} /> : null;
}

function Root() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <RootLogoutOverlay />
      <GlobalWidgets />
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/" element={<LandingRoute />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />

          <Route element={<CoursesShellGate />}>
            <Route path="/courses" element={<Courses />} />
          </Route>

          <Route element={<PublicShell />}>
            <Route path="/courses/:id" element={<CoursePreview />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/explore-activities" element={<ExploreActivities />} />
            <Route path="/books" element={<Books />} />
            <Route path="/book" element={<BookReview />} />
            <Route path="/free-screening" element={<FreeScreening />} />
            <Route path="/milestones" element={<MilestonesPage />} />
            <Route path="/milestones/:ageSlug" element={<MilestonesPage />} />
            <Route path="/child-age-calculator" element={<ChildAgeCalculator />} />
            <Route path="/ai-video-assessment" element={<VideoAssessment />} />
            <Route path="/videos" element={<Videos />} />
            <Route path="/success-stories" element={<SuccessStories />} />
            <Route path="/about" element={<About />} />
            <Route path="/faqs" element={<FAQs />} />
            <Route path="/help-center" element={<HelpCenter />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          <Route element={<PrivateRoute><AppShell /></PrivateRoute>}>
            <Route path="/onboarding" element={<ChildWizard />} />
            <Route path="/complete-profile" element={<CompleteProfile />} />
            <Route path="/child" element={<ChildProfileSummary />} />
            <Route path="/edit-profile" element={<EditProfile />} />
            {/* Legacy/external-link alias — the canonical route is
                `/onboarding`. Some marketing emails / older docs may still
                reference `/child-wizard`; redirect them silently. */}
            <Route path="/child-wizard" element={<Navigate to="/onboarding" replace />} />
            <Route path="/dashboard" element={<Activities />} />
            <Route path="/home" element={<Home />} />
            <Route path="/more" element={<More />} />
            <Route path="/today" element={<Navigate to="/activities" replace />} />
            <Route path="/assessment" element={<Assessment />} />
            <Route path="/report" element={<Report />} />
            <Route path="/deep-dive" element={<DeepDive />} />
            <Route path="/activities" element={<Activities />} />
            <Route path="/activities/library" element={<Library />} />
            <Route path="/activities/:id" element={<ActivityDetail />} />
            <Route path="/progress" element={<Progress />} />
            <Route path="/progress/:domainKey" element={<DomainDetail />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/courses/:id/learn" element={<CourseLearn />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/subscription" element={<Subscription />} />
            <Route path="/download-center" element={<DownloadCenter />} />
            <Route path="/disa" element={<Disa />} />
          </Route>
          <Route path="/reports/:month" element={<PrivateRoute><MonthlyReport /></PrivateRoute>} />
          <Route path="/admin" element={<PrivateRoute admin><Admin /></PrivateRoute>} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default function App() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
  return (
    <div className="App">
      <ErrorBoundary>
        <HelmetProvider>
          <ThemeProvider>
            <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
              <AuthProvider>
                <SavedProvider>
                  <Root />
                  <InstallPrompt />
                  <Toaster richColors position="top-center" />
                </SavedProvider>
              </AuthProvider>
            </GoogleOAuthProvider>
          </ThemeProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </div>
  );
}
